import { AnimatePresence } from 'framer-motion';
import { FC, useCallback, useState } from 'react';

import { Modal, ModalContent, ModalOverlay, useDisclosure } from '@bonusly/design-system';
import { useEvent } from '@bonusly/one-on-ones-queries';
import { useUser } from '@bonusly/user-queries';

import { CalendarIntegration } from '../calendar-integration';
import { DiscontinueModal } from './discontinue-modal';
import { EditMeetingContent } from './edit-meeting-content';

interface EditMeetingModalProps {
	eventId: string;
	guestId: string;
	isOpen: boolean;
	onClose: VoidFunction;
}

enum STEPS {
	CALENDAR_CONNECT = 'calendar-connect',
	MANUAL = 'manual',
}

export const EditMeetingModal: FC<EditMeetingModalProps> = ({ eventId, guestId, isOpen = false, onClose }) => {
	// NOTE: If we already created a meeting in the create-meeting-modal then both the guest and event will be in the cache and
	// no additional network requests will be made.
	const { data: guest } = useUser(guestId);

	// TOUR 5: Now that we've created an EventEntity, we can fetch it and use it throughout our components. The hook below, `useEvent`, is a global hook
	// that is part of a centralized collection of entity-fetching hooks. Instead of having a `type Event = {}` alias used, we can expect that the actual
	// JS class is passed around and the `EventEntity` is noted as the prop type in that case.
	// Just like in the create modal, we just need the ID of the entity to be able to fetch it - the entity itself doesn't have to be passed in because we
	// can fetch it from the query cache, or from the server if it's not there.
	//
	// How can we be sure that this process is efficent though?
	//
	// Well, let's check out the `useEvent` hook. Head over to `packages/api/queries/one-on-ones/src/hooks/event.ts`
	const { data: event } = useEvent(eventId);
	const discontinueModal = useDisclosure();

	const [step, setStep] = useState(STEPS.MANUAL);
	const setManualStep = useCallback(() => {
		setStep(STEPS.MANUAL);
	}, []);

	const setCalendarConnectStep = useCallback(() => {
		setStep(STEPS.CALENDAR_CONNECT);
	}, []);

	const onCloseModal = useCallback(() => {
		if (event?.externalCalendarLink) {
			setManualStep();
		} else {
			setCalendarConnectStep();
		}
		onClose();
	}, [onClose, setManualStep, setCalendarConnectStep, eventId, event?.externalCalendarLink]);

	// NOTE: If you check for the `data` property returned from `useQuery` calls instead of `isLoading` then TypeScript knows that below this line
	// both `guest` and `event` are not `undefined`. That means we don't need to decalare optional? props in the child components - we always know
	// that the data is there.
	if (!guest || !event) {
		return null;
	}

	return (
		<>
			<Modal autoFocus={false} closeOnOverlayClick={false} isOpen={isOpen} onClose={onCloseModal} scrollBehavior="outside" size="md" isCentered>
				<ModalOverlay />
				<ModalContent tabIndex={0}>
					<AnimatePresence mode="wait">
						{step === STEPS.MANUAL && (
							<EditMeetingContent
								event={event}
								onClose={onCloseModal}
								onDiscontinue={discontinueModal.onOpen}
								guest={guest}
								openLinkCalendar={setCalendarConnectStep}
							/>
						)}

						{step === STEPS.CALENDAR_CONNECT && <CalendarIntegration guestId={guest.id} onManual={setManualStep} onClose={onCloseModal} eventId={event.id} />}
					</AnimatePresence>
				</ModalContent>
			</Modal>

			{/*
				NOTE: Modals are opened in Portals so it's OK to have more than one in a component. But, we do need to be aware that
				the modal overlays will stack - the more modals you open the darker the screen around them will get. We should probably refactor this
				to either 1) close the edit modal when the discontinue modal is opened, or 2) make the discontinue option a popover or something that
				doesn't require a modal.
			*/}
			<DiscontinueModal event={event} guest={guest} {...discontinueModal} />
		</>
	);
};
